import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import Modal from './Modal';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /**
   * Сообщение — строка или ReactNode для composition (HintBox, links, etc.).
   * Composition over modification: domain-specific hint'ы передаются через JSX,
   * не через дополнительные props.
   */
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  isLoading?: boolean;
  loadingText?: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Подтвердить',
  cancelText = 'Отмена',
  isDestructive = false,
  isLoading = false,
  loadingText = 'Удаление...',
}) => {
  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="sm"
    >
      <div className="p-6">
        {/* Иконка предупреждения */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 'var(--space-16)',
          height: 'var(--space-16)',
          backgroundColor: isDestructive ? 'var(--error-100)' : 'var(--warning-100)',
          borderRadius: '50%',
          margin: '0 auto var(--space-4)',
        }}>
          {isDestructive ? (
            <X className="w-8 h-8" color="var(--error-600)" />
          ) : (
            <AlertTriangle className="w-8 h-8" color="var(--warning-600)" />
          )}
        </div>

        {/* Сообщение — string рендерится в <p> (legacy), ReactNode — как есть для composition */}
        <div className="text-center mb-6">
          {typeof message === 'string' ? (
            <p className="text-base text-gray-700 m-0" style={{ lineHeight: 'var(--leading-relaxed)' }}>
              {message}
            </p>
          ) : (
            <div className="text-base text-gray-700" style={{ lineHeight: 'var(--leading-relaxed)' }}>
              {message}
            </div>
          )}
        </div>

        {/* Действия */}
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="btn-secondary min-w-100"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className={`${isDestructive ? 'btn-danger' : 'btn-primary'} min-w-100`}
          >
            {isLoading ? (
              <>
                <div className="spinner" style={{
                  width: 'var(--space-4)',
                  height: 'var(--space-4)',
                  marginRight: 'var(--space-2)',
                }} />
                {loadingText}
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmModal;